/**
 * Copyright © 2024 Delicious AI, LLC
 */

import CloseIcon from '@mui/icons-material/Close'
import {
  Alert,
  AlertTitle,
  Box,
  Drawer,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import React, { useMemo, useRef, useState } from 'react'

import { BoundingBoxFragment, ProcessStatus } from '@/graphql'
import useT from '@/i18n/useT'

import CenteredBox from '../CenteredBox'
import ProductDetailsCard, { ProductInitialInfo } from '../ProductDetailsCard'
import {
  getProductPopperModifiers,
  getProductPopperPlacement,
  productPopoverContentSx,
  productPopperProps,
  StyledPopper,
  useProductPopover,
} from './use-product-popover'

export type Box = Pick<
  BoundingBoxFragment,
  'classifiedLabel' | 'detectedLabel' | 'minX' | 'minY' | 'maxX' | 'maxY' | 'id'
> & {
  color: string
}

type ImageWithBoxesProps = {
  source: string
  boxes: Box[]
  onMainImageError?: (error: string) => void
  style?: React.CSSProperties
  boxOpacity?: number
  contentFit?: 'cover' | 'contain'
  status: ProcessStatus
  errorMessage?: string | null
  showBoxes?: boolean
  imageStyle?: React.CSSProperties
  disableHover?: boolean
  renderPopoverContent?: (upc: string) => React.ReactNode
  productInfoByUpc?: Record<string, ProductInitialInfo>
}

const ImageWithBoxes: React.FunctionComponent<
  React.PropsWithChildren<ImageWithBoxesProps>
> = props => {
  const {
    source,
    boxes,
    onMainImageError,
    style,
    contentFit = 'contain',
    boxOpacity = 0.5,
    status,
    errorMessage,
    showBoxes = true,
    imageStyle = {},
    disableHover = false,
    renderPopoverContent,
    productInfoByUpc,
  } = props

  const theme = useTheme()
  const { t } = useT()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const {
    open,
    anchorEl,
    arrowRef,
    handleBoxClick,
    handleBoxMouseEnter,
    handleBoxMouseLeave,
    handlePopperMouseEnter,
    handlePopperMouseLeave,
    activeUpc,
    styles,
    setArrowRef,
  } = useProductPopover()

  const [mobileActiveUpc, setMobileActiveUpc] = useState<string | null>(null)

  const popperPlacement = useMemo(
    () => getProductPopperPlacement(anchorEl),
    [anchorEl],
  )

  const drawerContent = useMemo(() => {
    if (!mobileActiveUpc) return null
    if (renderPopoverContent) return renderPopoverContent(mobileActiveUpc)

    return (
      <ProductDetailsCard
        upc={mobileActiveUpc}
        hideDetails
        initialInfo={productInfoByUpc?.[mobileActiveUpc]}
      />
    )
  }, [mobileActiveUpc, productInfoByUpc, renderPopoverContent])

  const getBoxUpc = (box: Box) => box.classifiedLabel || box.detectedLabel

  const containerRef = useRef<HTMLDivElement>(null)
  const imageRef = useRef<HTMLImageElement>(null)

  const handleImageError = () => {
    onMainImageError?.('Failed to load image')
  }

  const baseStyles: React.CSSProperties = {
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
    width: 'max-content',
    ...style,
  }

  const finalImageStyle: React.CSSProperties = {
    maxWidth: '100%',
    maxHeight: '100vh',
    objectFit: contentFit,
    ...imageStyle,
  }

  return (
    <>
      <div ref={containerRef} style={baseStyles}>
        <img
          ref={imageRef}
          src={typeof source === 'string' ? source : ''}
          onError={handleImageError}
          style={finalImageStyle}
          alt={t('image.mainContent.alt')}
        />

        {status === ProcessStatus.FAILED ? (
          <CenteredBox sx={{ position: 'absolute' }}>
            <Alert severity="error">
              <AlertTitle>{t('boundingBox.error')}</AlertTitle>
              {errorMessage}
            </Alert>
          </CenteredBox>
        ) : status === ProcessStatus.PENDING ? (
          <CenteredBox sx={{ position: 'absolute' }}>
            <Alert severity="info">{t('boundingBox.pending')}</Alert>
          </CenteredBox>
        ) : showBoxes && boxes?.length ? (
          boxes.map((box, index) => {
            const upc = getBoxUpc(box)

            return (
              <Box
                key={box.id || index}
                sx={{
                  position: 'absolute',
                  top: `${box.minY * 100}%`,
                  left: `${box.minX * 100}%`,
                  width: `${(box.maxX - box.minX) * 100}%`,
                  height: `${(box.maxY - box.minY) * 100}%`,
                  border: `3px solid ${box.color}`,
                  boxSizing: 'border-box',
                  borderRadius: '4px',
                  cursor: disableHover ? 'default' : 'pointer',
                  transition: 'all 0.2s ease',
                }}
                onClick={() => {
                  if (disableHover) return

                  if (isMobile) {
                    setMobileActiveUpc(upc)
                  }
                }}
                onDoubleClick={
                  disableHover || isMobile
                    ? undefined
                    : event => {
                        handleBoxClick(event, upc)
                      }
                }
                onMouseEnter={
                  disableHover || isMobile
                    ? undefined
                    : event => {
                        handleBoxMouseEnter(event, upc)
                      }
                }
                onMouseLeave={
                  disableHover || isMobile ? undefined : handleBoxMouseLeave
                }
              >
                <Box
                  sx={{
                    opacity: boxOpacity,
                    background: box.color,
                    width: '100%',
                    height: '100%',
                  }}
                />
              </Box>
            )
          })
        ) : null}
      </div>
      <StyledPopper
        id="product-menu"
        anchorEl={anchorEl}
        open={!isMobile && open && !disableHover}
        placement={popperPlacement}
        {...productPopperProps}
        modifiers={getProductPopperModifiers(arrowRef)}
      >
        {activeUpc && (
          <>
            <Box
              component="span"
              className="arrow"
              ref={setArrowRef}
              sx={styles.arrow}
            />
            {renderPopoverContent ? (
              <Box
                sx={productPopoverContentSx}
                onMouseEnter={handlePopperMouseEnter}
                onMouseLeave={handlePopperMouseLeave}
              >
                {renderPopoverContent(activeUpc)}
              </Box>
            ) : (
              <Box
                sx={productPopoverContentSx}
                onMouseEnter={handlePopperMouseEnter}
                onMouseLeave={handlePopperMouseLeave}
              >
                <ProductDetailsCard
                  upc={activeUpc}
                  hideDetails
                  initialInfo={productInfoByUpc?.[activeUpc]}
                />
              </Box>
            )}
          </>
        )}
      </StyledPopper>
      <Drawer
        anchor="bottom"
        open={isMobile && !disableHover && Boolean(mobileActiveUpc)}
        onClose={() => setMobileActiveUpc(null)}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          zIndex: theme => theme.zIndex.modal + 2,
        }}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            maxHeight: '80vh',
            zIndex: theme => theme.zIndex.modal + 3,
          },
        }}
      >
        <Box sx={{ px: 2, pt: 1.5, pb: 3 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 1,
            }}
          >
            <Typography variant="subtitle2" color="text.secondary">
              {t('renderedPlanogram.drawer.title')}
            </Typography>
            <IconButton size="small" onClick={() => setMobileActiveUpc(null)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            {drawerContent}
          </Box>
        </Box>
      </Drawer>
    </>
  )
}

export default ImageWithBoxes
